import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import AxiosConfig from '../../../config/AxiosConfig';
import LoadingButton from '../utils/common/LoadingButton.jsx';
import UserAssignmentAutocomplete from '../utils/common/UserAssignmentAutocomplete.jsx';

const NewTicketDialog = (props) => {
  const {
    contacts,
    orders,
    openNewTicketDialog,
    setOpenNewTicketDialog,
    newTicketDialogMessage,
    setNewTicketDialogMessage,
    mutate,
  } = props;
  const navigate = useNavigate();
  const profile = useSelector((state) => state?.login?.profile);
  const services = useSelector((state) => state?.static?.services);
  const ticketTypes = useSelector((state) => state?.static?.ticketTypes);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSelectOptions, setOrderSelectOptions] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState('Standard');
  const [selectedUser, setSelectedUser] = useState(null);
  const [_userName, setUserName] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: mutateUsers } = useSWRConfig();

  useEffect(() => {
    if (contacts?.length > 0) {
      setSelectedContact(contacts[0]);
    }
  }, [contacts]);

  useEffect(() => {
    if (orders?.length > 0) {
      const defaultSelectedOrder = {
        _id: null,
        reference: 'N./A.',
      };
      setOrderSelectOptions([defaultSelectedOrder, ...orders]);
      if (selectedOrder === null) {
        setSelectedOrder(defaultSelectedOrder);
      }
    }
  }, [orders]);

  const handleNewTicketDialogClose = (_event) => {
    setOpenNewTicketDialog(false);
    setNewTicketDialogMessage(null);
    setSelectedService(null);
    setSelectedTicketType(null);
    setSelectedContact(contacts[0]);
    setSelectedOrder({
      _id: null,
      reference: 'N./A.',
    });
    setSelectedPriority('Standard');
    setUserName('');
    setSelectedUser(null);
    // setNoteContent("");
  };

  const handleServiceChange = (e, value) => {
    if (selectedService?._id !== value?._id) {
      setSelectedService(value);
      setSelectedTicketType(null);
    }
  };

  const handleTicketTypeChange = (e, value) => {
    if (selectedTicketType?._id !== value?._id) {
      setSelectedTicketType(value);
    }
  };

  const handleContactChange = (e, value) => {
    if (selectedContact?._id !== value?._id) {
      setSelectedContact(value);
    }
  };

  const handleOrderChange = (e, value) => {
    if (selectedOrder?._id !== value?._id) {
      setSelectedOrder(value);
    }
  };

  const handlePriorityChange = (e, value) => {
    if (selectedPriority !== value) {
      setSelectedPriority(value);
    }
  };

  const handleNoteTextChange = (event) => {
    setNoteContent(event.target.value);
  };

  const getContactChannelType = () => {
    const sellerName = selectedContact?.store?.seller?.name?.toLowerCase();
    return ['hoortrade', 'manomano', 'amazon'].includes(sellerName)
      ? 'MAIL'
      : 'RABBITMQ';
  };

  const handleNewTicketDialogConfirm = (_event) => {
    setIsLoading(true);
    AxiosConfig.create('post', '/tickets/')
      .call({
        serviceId: selectedService?._id,
        ticketTypeId: selectedTicketType?._id,
        contactId: selectedContact?._id,
        orderId: selectedOrder?._id ? selectedOrder?._id : null,
        messageId: newTicketDialogMessage?._id,
        assignedBy: profile?._id,
        assignedTo: selectedUser ? selectedUser?._id : null,
        priorityName: selectedPriority,
        noteContent: noteContent?.trim(),
        contactChannelType: getContactChannelType(),
      })
      .exit((res) => {
        const ticket = res?.data;
        setSelectedUser(res?.data?.assignedTo);
        mutate();
        mutateUsers(`/users/`);
        setIsLoading(false);
        navigate(
          `/project/${ticket?.project?._id}/tickets/${ticket?._id}/activities`
        );
      })
      .catch((err) => console.error(err))
      .finally(() => {
        handleNewTicketDialogClose();
      });
  };

  return (
    <>
      <Dialog
        open={openNewTicketDialog}
        onClose={handleNewTicketDialogClose}
        maxWidth={'lg'}
        fullWidth
      >
        <DialogTitle>Nouveau ticket</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {newTicketDialogMessage ? (
              <Typography component={'span'}>
                {newTicketDialogMessage?.snippet}
              </Typography>
            ) : null}
          </DialogContentText>
          <Grid
            container
            sx={{ display: 'flex', marginTop: 2, alignItems: 'center' }}
          >
            <Grid size="grow">
              {services ? (
                <>
                  <Autocomplete
                    id="select-service"
                    options={
                      services?.filter(
                        (service) =>
                          ['SCO', 'SLO', 'SAV'].indexOf(service?.code) !== -1
                      ) || []
                    }
                    getOptionLabel={(option) => {
                      return `${option?.code} - ${option?.name}`;
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option?._id === value?._id;
                    }}
                    filterOptions={(options, _state) => options}
                    value={selectedService}
                    onChange={handleServiceChange}
                    sx={{ width: 250 }}
                    disableClearable={true}
                    renderInput={(params) => (
                      <TextField {...params} label="Service" />
                    )}
                  />{' '}
                </>
              ) : null}
            </Grid>
            <Grid size="grow">
              {ticketTypes ? (
                <>
                  <Autocomplete
                    id="select-tickettype"
                    options={
                      ticketTypes?.filter(
                        (ticketType) =>
                          ticketType?.service?._id === selectedService?._id
                      ) || []
                    }
                    getOptionLabel={(option) => {
                      return `${option?.name}`;
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option?._id === value?._id;
                    }}
                    filterOptions={(options, _state) => options}
                    value={selectedTicketType}
                    onChange={handleTicketTypeChange}
                    sx={{ width: 250 }}
                    disableClearable={true}
                    renderInput={(params) => (
                      <TextField {...params} label="Type" />
                    )}
                  />
                </>
              ) : null}
            </Grid>
            {contacts?.length >= 1 ? (
              <Grid size="grow">
                <Autocomplete
                  id="select-contact"
                  options={contacts || []}
                  getOptionLabel={(option) => {
                    return `${option?.store?.shortName} - ${option?.email}`;
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return option?._id === value?._id;
                  }}
                  filterOptions={(options, _state) => options}
                  value={selectedContact}
                  onChange={handleContactChange}
                  sx={{ width: 250 }}
                  disableClearable={true}
                  renderInput={(params) => (
                    <TextField {...params} label="Contact" />
                  )}
                />
              </Grid>
            ) : null}
            {orderSelectOptions?.length >= 1 ? (
              <Grid size="grow">
                <Autocomplete
                  id="select-order"
                  options={
                    orderSelectOptions || []
                    // .filter((option) => option?._id)
                  }
                  getOptionLabel={(option) => {
                    return `${option?.reference}`;
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return option?._id === value?._id;
                  }}
                  filterOptions={(options, _state) => options}
                  value={selectedOrder}
                  onChange={handleOrderChange}
                  sx={{ width: 250 }}
                  disableClearable={true}
                  renderInput={(params) => (
                    <TextField {...params} label="N° Commande" />
                  )}
                />
              </Grid>
            ) : null}
          </Grid>
          <Grid
            container
            sx={{ display: 'flex', marginTop: 2, alignItems: 'center' }}
          >
            <Grid size={3}>
              <Autocomplete
                id="select-priority"
                options={['Standard', 'Haute', 'Critique']}
                getOptionLabel={(option) => {
                  return option;
                }}
                isOptionEqualToValue={(option, value) => {
                  return option === value;
                }}
                filterOptions={(options, _state) => options}
                value={selectedPriority}
                onChange={handlePriorityChange}
                sx={{ width: 250 }}
                disableClearable={true}
                renderInput={(params) => (
                  <TextField {...params} label="Priorité" />
                )}
              />
            </Grid>
            <Grid sx={{ border: 0 }} size={3}>
              <UserAssignmentAutocomplete
                type={'ticket'}
                setSelectedUser={setSelectedUser}
                selectedUser={selectedUser}
                isSubmit={true}
              />
            </Grid>
            <Grid sx={{ flexGrow: 1 }} size={6}>
              <TextField
                id="filled-textarea"
                label="Note"
                placeholder="Ecrire le motif..."
                multiline
                sx={{ width: '95%' }}
                rows={3}
                onChange={handleNoteTextChange}
                variant="outlined"
                helperText={
                  selectedUser?._id !== profile?._id &&
                  noteContent?.length === 0
                    ? 'La note est obligatoire !'
                    : ''
                }
                error={
                  selectedUser?._id !== profile?._id &&
                  noteContent?.length === 0
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNewTicketDialogClose}>Annuler</Button>
          <LoadingButton
            isLoading={isLoading}
            onClick={handleNewTicketDialogConfirm}
            disabled={
              selectedTicketType === null ||
              (selectedUser?._id !== profile?._id &&
                noteContent?.trim()?.length === 0)
            }
          >
            Confirmer
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewTicketDialog;
